<script>
import $ from 'jquery';
import BellIcon from '../../icons/BellIcon.vue';

export default {
  components: {
    BellIcon,
  },

  props: {
    regions: {
      type: Object,
      required: true,
    },
    contact: {
      type: Object,
      required: false,
      default: () => ({
        details: {},
        regions: [],
      }),
    },
  },

  data: () => ({
    input: {
      regions: [],
      firstName: '',
      lastName: '',
      email: '',
      organization: '',
      consent: false,
    },

    errors: [],
  }),

  methods: {
    async addNotifications() {
      if (!this.input.consent) {
        this.$toasted.error(
          'You must consent to receive notifications from us in order to receive updates about Financial Incentives.',
          {
            duration: 2000,
          },
        );
        return;
      }

      if (this.input.regions.length === 0) {
        this.$toasted.error(
          'You need to select at least one region to subscribe to notifications.',
          {
            duration: 2000,
          },
        );
        return;
      }

      if (
        this.input.firstName.length === 0
                || this.input.lastName.length === 0
                || this.input.email.length === 0
      ) {
        this.$toasted.error(
          'You need to provide your full name and email address in order to subscribe to notifications.',
          {
            duration: 2000,
          },
        );
        return;
      }

      try {
        const data = {
          firstName: this.input.firstName,
          lastName: this.input.lastName,
          email: this.input.email,
          organization: this.input.organization,
          regions: this.input.regions.map(
            ({
              region,
              country,
            }) => ({
              country: country.code,
              region: region.code,
            }),
          ),
        };

        await axios.post('api/v1/financial-incentives/notify', data);

        $('#subscribe')
          .modal('hide');

        this.$toasted.success(
          'You have successfully subscribed to notifications about Financial Incentives in your selected regions.',
          {
            duration: 2000,
          },
        );
      } catch (e) {
        this.$toasted.error(
          'An unexpected error has occurred.',
        );
      }
    },
  },

  mounted() {
    const allRegions = this.regions;

    this.input = {
      ...this.contact.details,
      regions: (this.contact.regions ?? [])
        .map(({
          region: regionCode,
          country: countryCode,
        }) => {
          const specifiedCountry = allRegions[countryCode];

          if (!specifiedCountry) {
            return null;
          }

          return {
            country: specifiedCountry.country,
            region: Object.values(specifiedCountry.regions)
              .find((r) => r.code === regionCode),
          };
        })
        .filter((f) => f),
    };

    if (this.contact.details.email) {
      $('#subscribe')
        .modal('show');
    }
  },
};
</script>

<template>
    <div>
        <!-- Modal -->
        <div id="subscribe" ref="subscribeModal" aria-hidden="true" aria-labelledby="subscribeModalTitle"
             class="modal fade"
             role="dialog" tabindex="-1">
            <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 id="subscribeModalTitle" class="modal-title">Notify me with Updates</h5>
                        <button aria-label="Close" class="close" data-dismiss="modal" type="button">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <form method="post" style="margin-bottom: 4px;" @submit.prevent="addNotifications">
                            <div class="input-group pb-1">
                                <div class="input-group-prepend">
                                    <label class="input-group-text" for="firstName">
                                        First Name
                                        <span style="color: red">*</span>
                                    </label>
                                </div>
                                <input id="firstName" v-model.trim="input.firstName" class="form-control" required/>
                            </div>
                            <div class="input-group pb-1">
                                <div class="input-group-prepend">
                                    <label class="input-group-text" for="lastName">
                                        Last Name
                                        <span style="color: red">*</span>
                                    </label>
                                </div>
                                <input id="lastName" v-model.trim="input.lastName" class="form-control" required/>
                            </div>
                            <div class="input-group pb-1">
                                <div class="input-group-prepend">
                                    <label class="input-group-text" for="emailAddress">
                                        Email Address
                                        <span style="color: red">*</span>
                                    </label>
                                </div>
                                <input id="emailAddress" v-model.trim="input.email" class="form-control" required
                                       type="email"/>
                            </div>
                            <div class="input-group pb-1">
                                <div class="input-group-prepend">
                                    <label class="input-group-text" for="organization">
                                        Organization
                                    </label>
                                </div>
                                <input id="organization" v-model.trim="input.organization" class="form-control"/>
                            </div>
                            <div class="input-group pb-1">
                                <div class="input-group-prepend">
                                    <label class="input-group-text" for="regions">
                                        Regions
                                        <span style="color: red">*</span>
                                    </label>
                                </div>
                                <select id="regions" v-model="input.regions" class="form-control"
                                        multiple="multiple"
                                        required
                                        size="10"
                                        title="Select one or more regions"
                                >
                                    <optgroup v-for="{country, regions} in regions" :key="country.name"
                                              :label="country.name">
                                        <option v-for="region in regions" :key="`${country.name}-${region.name}`"
                                                :value="{country, region}">
                                            {{ region.name }}
                                        </option>
                                    </optgroup>
                                </select>
                            </div>
                            <div class="input-group pb-1">
                                <input id="consent" v-model="input.consent" type="checkbox"/>
                                <label class="form-label" for="consent" style="font-size: 14px; margin: 5px;">
                                    Send me email notifications about new energy grant campaigns. I can unsubscribe at
                                    any time.
                                </label>
                            </div>
                            <div style="clear: both;"></div>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-secondary" data-dismiss="modal" type="button">Close</button>
                        <button class="btn btn-primary" type="button" @click.prevent="addNotifications">
                            Keep me Updated!
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <button class="btn btn-outline-secondary" data-target="#subscribe" data-toggle="modal">
            <BellIcon/>
            Notify Me
        </button>
    </div>
</template>
